<template>
    <div class="data-entry">

        <b-card :title="cardTitle">

            <template slot="body">

                <div class="card-body">
                    <section class="row mb-4">

                        <!-- DESCRIPTION -->
                        <div class="col-md-6">
                            <span class="card-subtitle">Description</span>
                            <p v-for="(paragraph, idx) in textToParagraphs(dataSet.description)" :key="idx">{{ paragraph }}</p>
                        </div>

                        <!-- ENTITY, OWNER, COLLECTOR -->
                        <div class="col-md-6">
                            <span class="card-subtitle">Stakeholders</span>
                            <dl class="row">
                                <!-- ENTITY -->
                                <dt class="col-md-6">Entity:</dt><dd class="col-md-6">{{ dataSet.entity.name }}</dd>
                                <!-- OWNER -->
                                <dt class="col-md-6">Owner:</dt><dd class="col-md-6">{{ dataSet.owner.name }}</dd>
                            </dl>
                             <!-- COLLECTOR -->
                            <!-- <dl class="row">
                               
                                <dt class="col-md-6">Collector:</dt>
                                <dd v-if="dataSet.collector != null" class="col-md-6">
                                    {{ (dataSet.collector.collector != null) ? dataSet.collector.collector.name : '/' }}
                                </dd>
                            </dl> -->
                            <dl class="row">
                                <!-- form they are using -->
                                <dt class="col-md-6">Using data set type:</dt>
                                <dd class="col-md-6">{{ dataSet.data_set_type.name }}</dd>
                            </dl>
                        </div>

                    </section>
                    <section class="row mb-4">

                        <!-- PERIOD -->
                        <div class="col-md-6">
                            <span class="card-subtitle">Period</span>
                            {{ (dataSet.data_set_type.period) ? dataSet.data_set_type.period.name : 'None' }}
                        </div>

                        <!-- DATEPICKER -->
                        <div class="col-md-6">
                            <div class="col-md-6" v-if="dataSet.data_set_type.period.period_length !== 0">

                                <span class="card-subtitle">Valid For</span>

                                <div  v-if="dataSet.data_set_type.period.period_length === 12">
                                    <year-picker
                                        name="valid_for_period"
                                        :default-date="getDefaultDate()"
                                        :disabled-years="{ from: this.currentYearDate() }"
                                        v-model="validFor" >
                                    </year-picker>
                                </div>

                                <div v-else>
                                    <month-picker
                                        name="valid_for_period"
                                        :default-date="getDefaultDate()"
                                        :disabled-months="{ from: this.pastMonthDate() }"
                                        v-model="validFor" >
                                    </month-picker>
                                </div>

                            </div>
                        </div>

                    </section>
                </div>

            </template>

        </b-card>

        <b-card title="Form">

            <template slot="body">

                <div class="card-body m-5" v-if="loadingData"> <loading-spinner :loading="loadingData"></loading-spinner> </div>

                <div class="card-body" v-else-if="!collectionSheet">
                    <p class="text-danger">
                        Sorry, we could not fetch the appropriate data collection form.
                        Please try again later or contact your administrator if the problem persists.
                    </p>
                </div>

                <div class="card-body row" v-else>
                    <entry-form :collection-sheet="collectionSheet"></entry-form>

                    <div v-if="success !== null" class="text-success" style="display : block"> <p>{{ success }}</p> </div>

                    <div v-if="error !== null" class="invalid-feedback" style="display : block"> <p>{{ error }}</p> </div>
                </div>

            </template>

            <template slot="footer">
                <div class="text-right">
                    <div
                        class="btn btn-primary border border-dark"
                        @click="saveModifiedValues"
                        :disabled="loadingData || !collectionSheet || collectionSheet.getModifiedValues().length <= 0">
                        Save
                    </div>
                </div>
            </template>

        </b-card>

    </div>
</template>

<script>

import LoadingSpinner from '../../common/LoadingSpinner';
import MonthPicker from '../../common/MonthPicker';
import YearPicker from '../../common/YearPicker';
import EntryForm from './EntryForm';
import Utils from '../../mixins/UtilsMixin'; // used within defaultDate generation
import cs from './CollectionSheet';
import moment from 'moment';
import CollectionSheet from './CollectionSheet';

export default {
    components: {
        "month-picker": MonthPicker,
        "year-picker": YearPicker,
        "loading-spinner": LoadingSpinner,
        "entry-form": EntryForm,
    },

    mixins: [Utils],

    props: {

        dataSet: {
            type: Object,
            required: true,
        }

    },

    data() {
        return {
            loadingData: false,
            validFor: null,
            collectionSheet: null,
            error: null,
            success: null,
        };
    },

    computed: {

        cardTitle() {
            return "Data Entry :: " + this.dataSet.name;
        }

    },

    watch: {

        // Load new collection sheet with previous data when valid for date changes.
        validFor() {
            if (!this.loadingData) {
                this.loadingData = true;
                this.loadValidForDateData();
            }
        }

    },

    mounted() {
        this.initializeForm();
    },

    methods: {

        initializeForm() {
            this.loadingData = true;

            this.validFor = this.getDefaultDate();

            this.loadValidForDateData();

        },

        loadValidForDateData() {
            let ajaxCalls = [
                this.getCollectionSheetAxios(),
                this.getValuesAxios(),
            ];

            let onSuccess = (collectionSheet, values) => {
                this.collectionSheet = new CollectionSheet(collectionSheet.data);
                this.collectionSheet.newValuesToCollectionSheet(values.data, this.validFor);
            };

            window.axios.all(ajaxCalls)
                .then(window.axios.spread(onSuccess))
                .catch((err) => {
                    this.collectionSheet = null; // will trigger message
                    window.console.error(err);
                })
                .then(() => { this.loadingData = false; });
        },

        saveModifiedValues() {
            this.postValues();
        },

        getDefaultDate() {
            let queryParams = this.decodeURLParams();

            if (queryParams.hasOwnProperty('date')) { return this.roundDateToPeriod(queryParams.date, this.dataSet.data_set_type.period.name.charAt(0)); }

            if (this.dataSet.data_set_type.period.period_length === 12) { return this.currentYearDate(); }

            if (this.dataSet.data_set_type.period.period_length === 1) { return this.pastMonthDate(); }

            return new Date();
        },

        postValues() {
            if (this.loadingData || !this.collectionSheet) return;

            let values = [];
            let modifiedValues = this.collectionSheet.getModifiedValues();

            if (modifiedValues.length <= 0) {
                window.alert('No values changed.');
                return;
            }

            this.loadingData = true;
            this.success = null;
            this.error = null;

            // fill valid_for_date and change to right format
            for (let key in modifiedValues) {
                if (modifiedValues.hasOwnProperty(key)) {
                    modifiedValues[key].valid_for_date = moment(this.validFor).format('Y-MM');
                    values.push(modifiedValues[key]);
                }
            }

            window.axios.post(`${APP_URL}/data-collection/entry/${this.dataSet.id}/values`, values)
                .then((response) => {
                    this.collectionSheet.clearEmptyErrors();
                    this.collectionSheet.valuesToCollectionSheet(response.data.success, this.validFor);
                    this.collectionSheet.valuesToCollectionSheet(response.data.errors, this.validFor, false);

                    if (response.data.success.length > 0) this.success = 'Values saved: ' + response.data.success.length;

                    if (response.data.errors.length > 0) this.error = 'Values not saved: ' + response.data.errors.length;
                })
                .catch((err) => {
                    this.collectionSheet.clearErrors();
                    this.success = null;
                    this.error = 'Something went wrong. Data not saved.';
                })
                .then(() => { this.loadingData = false; });
        },

        getValuesAxios() {
            let m = moment(this.validFor).format('Y-MM');

            if (this.dataSet.data_set_type.period.name === "Monthly")  m = moment(this.validFor).format('Y-MM');

            return window.axios.get(`${APP_URL}/data-collection/entry/${this.dataSet.id}/values?for=${m}`);
        },

        getCollectionSheetAxios() {
            return window.axios.get(`${APP_URL}/data-collection/entry/${this.dataSet.id}/collection-sheet`);
        }

    }

};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/measurements-module/entry/DataEntry.vue